import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Menu, X, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LakehouseLogo from "../../assets/LakehouseLogo.svg";

export default function Navigation({ activeSection, scrollToSection, language, setLanguage, texts }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    { id: 'home', label: texts.home },
    { id: 'products', label: texts.products },
    { id: 'services', label: texts.services },
    { id: 'team', label: texts.team },
    { id: 'about', label: texts.about },
    { id: 'contact', label: texts.contact }
  ];

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setIsLangOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-lg border-b border-gray-800/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => scrollToSection('home')}
          >
            <img 
              src={LakehouseLogo} 
              alt="Lakehouse Logo" 
              className="w-14 h-14 object-contain"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Lakehouse
            </span>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`relative text-sm font-medium transition-colors duration-200 ${
                  activeSection === item.id 
                    ? 'text-blue-400' 
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {item.label}
                {activeSection === item.id && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500"
                    transition={{ duration: 0.3 }}
                  />
                )}
              </button>
            ))}
            <div className="w-px h-6 bg-gray-700"></div>
            <div className="relative" ref={langRef}>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-300 hover:text-white"
                onClick={() => setIsLangOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={isLangOpen}
                aria-label="Change language"
              >
                <Globe className="h-5 w-5" />
              </Button>
              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-gray-900 border border-gray-700 text-white shadow-lg ring-1 ring-black/50 focus:outline-none z-50">
                  <div className="py-1">
                    <button onClick={() => handleLanguageChange('en')} className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-800">
                      EN (English)
                    </button>
                    <button onClick={() => handleLanguageChange('ro')} className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-800">
                      RO (Română)
                    </button>
                  </div>
                </div>
              )}
            </div>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 py-2 rounded-lg transition-all duration-300"
            >
              {texts.getStarted}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-300 hover:text-white transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-black/95 backdrop-blur-xl border-t border-gray-800/50"
          >
            <div className="px-6 py-4 space-y-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    scrollToSection(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left text-sm font-medium py-2 transition-colors duration-200 ${
                    activeSection === item.id 
                      ? 'text-blue-400' 
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-4 border-t border-gray-800 flex justify-between items-center">
                <span className="text-sm text-gray-400">{texts.language}:</span>
                <div>
                  <Button variant="ghost" size="sm" onClick={() => handleLanguageChange('en')} className={`${language === 'en' ? 'text-blue-400' : 'text-gray-300'}`}>EN</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleLanguageChange('ro')} className={`${language === 'ro' ? 'text-blue-400' : 'text-gray-300'}`}>RO</Button>
                </div>
              </div>
              <Button
                onClick={() => {
                  scrollToSection('contact');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 py-2 rounded-lg transition-all duration-300"
              >
                {texts.getStarted}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
